// React&NextJS
import React, { useContext, useEffect } from 'react'
// Context&Auth
import { UIContext } from '../../../context/UIContext'
// 3rd party libraries
import { useAnimation, AnimatePresence, motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
// Services&Helper functions
import { useKeyDown } from '../../../hooks/useKeyDown'
// Styles
import s from './Modal.module.scss'
// Components
import { Button } from '../../core/button/Button'
import { Container } from '../../core/container/Container'
import { Text } from '../../core/text/Text'
import { WhiteBox } from '../../core/whiteBox/WhiteBox'

type Props = {
    title: string
    info?: string
    data?: {
        fullName: string
        birth: string
        death: string
        [key: string]: unknown
    }
    isOpen?: boolean
    closeModal: Function
    closeEvent?: Function
    submitEvent?: Function
    secondaryBtn?: string
    primaryBtn?: string
}

const ActionModal = ({
    title,
    info,
    data,
    isOpen,
    closeModal,
    closeEvent,
    submitEvent,
    secondaryBtn,
    primaryBtn,
}: Props) => {
    const { uiState, setUIState } = useContext(UIContext)
    const [ref, inView] = useInView()
    const controls = useAnimation()

    useEffect(() => {
        if (isOpen) {
            setUIState({ ...uiState, canScroll: false })
        } else {
            setUIState({ ...uiState, canScroll: true })
        }
        return () => {
            setUIState({ ...uiState, canScroll: true })
        }
    }, [isOpen])

    useKeyDown(['Escape'], ({ key }) => {
        if (key === 'Escape' && isOpen) {
            closeModal()
        }
    })

    useEffect(() => {
        if (inView) {
            controls.start('visible')
        }
    }, [inView, controls])

    const checkIfClosing = (e: React.BaseSyntheticEvent) => {
        if (e.target?.parentNode?.id === 'background') {
            closeModal()
        }
    }

    if (isOpen) {
        return (
            <AnimatePresence>
                <motion.div
                    className={s.background}
                    onMouseDown={(e) => checkIfClosing(e)}
                    id="background"
                    ref={ref}
                    animate={isOpen ? 'visible' : 'hidden'}
                    initial="hidden"
                    exit="hidden"
                    transition={{
                        duration: 0.2,
                        ease: 'easeOut',
                    }}
                    variants={{
                        visible: {
                            opacity: 1,
                        },
                        hidden: {
                            opacity: 0,
                        },
                    }}
                >
                    <Container className={s.container}>
                        <WhiteBox className={s.modal}>
                            <div>
                                <Text variant="h2">{title}</Text>
                                {info && <Text variant="p-18">{info}</Text>}
                                {data && (
                                    <>
                                        <Text align="center" variant="date">
                                            {data.fullName}
                                        </Text>
                                        <Text
                                            align="center"
                                            className={s.dates}
                                            variant="date"
                                        >
                                            {`Fæðingardagur: ${data.birth} - Dánardagur: ${data.death}`}
                                        </Text>
                                    </>
                                )}
                            </div>
                            <div className={s.buttons}>
                                <Button
                                    variant="ghost"
                                    type="button"
                                    onClick={() => {
                                        if (closeEvent) closeEvent()
                                        else closeModal()
                                    }}
                                    className={s.button}
                                >
                                    {secondaryBtn ?? 'Hætta við'}
                                </Button>
                                {submitEvent && (
                                    <Button
                                        type="button"
                                        className={s.button}
                                        onClick={() => submitEvent()}
                                    >
                                        {primaryBtn ?? 'Staðfesta'}
                                    </Button>
                                )}
                            </div>
                        </WhiteBox>
                    </Container>
                </motion.div>
            </AnimatePresence>
        )
    }
    return null
}

export default ActionModal
